import { useEffect, useState } from 'react';

import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import type { Blob } from '@rails/activestorage';
import { Typography } from 'antd';
import type { TablePaginationConfig } from 'antd/es/table/interface';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router';

import {
  useGetSchoolParticipantsQuery,
  useUploadNewParticipantsMutation,
} from '@/api/endpoints/schools/schoolParticipants';
import { CustomTable, FetchDataWrapper } from '@/shared/components';
import { FileUploadField } from '@/shared/components/Fields/FileUploadField';
import { checkIsSpreadSheetFileValid } from '@/shared/helpers';
import { useApiRequest, useLoader } from '@/shared/hooks';

import { ESelectionPanelTabs } from '../../../_components/SelectionPanelHeader/consts';
import { SelectionPanelHeader } from '../../../_components/SelectionPanelHeader/SelectionPanelHeader';

import { getColumnsData } from './helpers';
import type { IFormData } from './types';
import styles from './UsersPage.module.scss';

export const UsersPage = () => {
  const [testingTablePagination, setTestingTablePagination] = useState<TablePaginationConfig>({
    pageSize: 10,
    current: 1,
    total: 0,
  });

  const { id: schoolId } = useParams();

  const [uploadNewParticipants] = useUploadNewParticipantsMutation();

  const { hideLoader, showLoader } = useLoader();
  const sendApiRequest = useApiRequest();

  const {
    data: participantsData,
    isLoading: areParticipantsLoading,
    isFetching: areParticipantsFetching,
    isError: isParticipantsFetchError,
  } = useGetSchoolParticipantsQuery({
    schoolId: Number(schoolId),
    page: testingTablePagination.current,
    perPage: testingTablePagination.pageSize,
  });

  const methods = useForm<IFormData>({
    defaultValues: {
      file: null,
    },
  });

  const columns = getColumnsData();

  useEffect(() => {
    if (participantsData?.meta.pagination.total_count) {
      setTestingTablePagination((prev) => ({
        ...prev,
        total: participantsData.meta.pagination.total_count,
      }));
    }
  }, [participantsData?.meta.pagination.total_count]);

  useEffect(() => {
    if (areParticipantsFetching) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [areParticipantsFetching]);

  const uploadImportFile = (blob: Blob) => {
    sendApiRequest({
      successMessage: 'Файл успешно загружен',
      apiRequestCallback: () => uploadNewParticipants({ schoolId: Number(schoolId), file: blob.signed_id }),
    });
  };

  return (
    <FetchDataWrapper
      isLoading={areParticipantsLoading}
      isError={isParticipantsFetchError}
    >
      {participantsData && (
        <FormProvider {...methods}>
          <div className={styles.wrapper}>
            <SelectionPanelHeader currentTab={ESelectionPanelTabs.Users} />
            <div className={styles['page-info-wrapper']}>
              <p className={styles.title}>Список всех пользователей</p>
              <p className={styles.description}>В списке отображаются импортированные в школу пользователи</p>
            </div>
            <FileUploadField
              acceptFormats=".csv,.xlsx"
              fieldName="file"
              fileCheckCb={checkIsSpreadSheetFileValid}
              requestCallback={uploadImportFile}
              className={styles.button}
            >
              <UploadOutlined className={styles['import-image']} />
              <p className={styles['import-message']}>Импорт новых участников</p>
              <p className={styles['import-message']}>Перетащите сюда файл или нажмите, чтобы выбрать его</p>
              <div className={styles['import-requirements-wrapper']}>
                <p>Поддерживаемые форматы: CSV, XLSX</p>
                <p>Максимальный размер файла: 10 МБ</p>
              </div>
              <Typography.Link
                className={styles['download-example-link']}
                href="/files/schoolParticipantsImport/new-participants-import-example.xlsx"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <DownloadOutlined />
                Скачать шаблон файла
              </Typography.Link>
            </FileUploadField>
            <CustomTable
              rowKey="id"
              pagination={{ ...testingTablePagination }}
              dataSource={participantsData.data}
              onChange={(pagination, _, __, extra) => {
                setTestingTablePagination({
                  ...pagination,
                  ...(extra.action === 'paginate' && pagination.pageSize !== testingTablePagination.pageSize
                    ? { current: 1 }
                    : {}),
                });
              }}
              columns={columns}
            />
          </div>
        </FormProvider>
      )}
    </FetchDataWrapper>
  );
};
